import { useState, useRef, useCallback, DragEvent, ChangeEvent } from 'react';
import { Plus, X, Upload, AlertCircle, Loader2, Image as ImageIcon, Link } from 'lucide-react';
import { Button } from '@/frontend/components/ui/button';
import { Input } from '@/frontend/components/ui/input';
import { Label } from '@/frontend/components/ui/label';
import { Progress } from '@/frontend/components/ui/progress';
import { cn } from '@/frontend/lib/utils';
import { uploadImage, validateImageFile } from '@/backend/services/storage/images';

export interface MultiImageUploadFieldProps {
  values: string[];
  onChange: (urls: string[]) => void;
  storagePathPrefix: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
}

interface UploadingFile {
  id: string;
  name: string;
  progress: number;
  isCompressing: boolean;
}

export function MultiImageUploadField({
  values,
  onChange,
  storagePathPrefix,
  label,
  required = false,
  disabled = false,
  onUploadingChange,
}: MultiImageUploadFieldProps) {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [previewErrors, setPreviewErrors] = useState<Set<number>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isUploading = uploadingFiles.length > 0;

  const notifyUploading = useCallback((count: number) => {
    onUploadingChange?.(count > 0);
  }, [onUploadingChange]);

  // Upload a single file, returning the URL on success
  const uploadSingleFile = async (file: File, uploadId: string): Promise<string | null> => {
    const validationError = validateImageFile(file);
    if (validationError) {
      setError(prev => prev ? `${prev}\n${file.name}: ${validationError}` : `${file.name}: ${validationError}`);
      return null;
    }

    setUploadingFiles(prev => [...prev, { id: uploadId, name: file.name, progress: 0, isCompressing: true }]);

    try {
      const result = await uploadImage({
        file,
        storagePath: storagePathPrefix,
        onProgress: (progress) => {
          setUploadingFiles(prev =>
            prev.map(f => f.id === uploadId ? { ...f, progress, isCompressing: false } : f)
          );
        },
      });
      return result.url;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setError(prev => prev ? `${prev}\n${file.name}: ${msg}` : `${file.name}: ${msg}`);
      return null;
    } finally {
      setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
    }
  };

  // Handle multiple files at once
  const handleFiles = async (files: FileList) => {
    setError('');
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    const totalUploads = fileArray.length;
    notifyUploading(totalUploads);

    // Start all uploads in parallel
    const uploadPromises = fileArray.map((file, i) => {
      const uploadId = `${Date.now()}-${i}`;
      return uploadSingleFile(file, uploadId);
    });

    const results = await Promise.all(uploadPromises);
    const newUrls = results.filter((url): url is string => url !== null);

    if (newUrls.length > 0) {
      onChange([...values, ...newUrls]);
    }

    notifyUploading(0);
  };

  // Drag & drop handlers
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled || isUploading) return;
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBrowseClick = () => {
    if (!disabled && !isUploading) fileInputRef.current?.click();
  };

  const handleAddUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    try {
      new URL(trimmed);
      onChange([...values, trimmed]);
      setUrlInput('');
      setError('');
    } catch {
      setError('Please enter a valid URL');
    }
  };

  const handleRemove = (index: number) => {
    setPreviewErrors(prev => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
    onChange(values.filter((_, i) => i !== index));
  };

  const handlePreviewError = (index: number) => {
    setPreviewErrors(prev => new Set(prev).add(index));
  };

  return (
    <div className="space-y-3">
      {label && (
        <Label>
          {label} {required && '*'}
        </Label>
      )}

      {/* Existing images grid */}
      {values.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {values.map((url, index) => (
            <div key={index} className="relative group aspect-square">
              {previewErrors.has(index) ? (
                <div className="w-full h-full rounded border bg-muted flex items-center justify-center">
                  <ImageIcon className="h-6 w-6 text-muted-foreground" />
                </div>
              ) : (
                <img
                  src={url}
                  alt={`Image ${index + 1}`}
                  className="w-full h-full object-cover rounded border"
                  onError={() => handlePreviewError(index)}
                />
              )}
              {!disabled && (
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleRemove(index)}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Currently uploading files */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          {uploadingFiles.map(file => (
            <div key={file.id} className="flex items-center gap-3 p-2 border rounded-lg bg-primary/5">
              <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{file.name}</p>
                {file.isCompressing ? (
                  <p className="text-xs text-muted-foreground">Compressing...</p>
                ) : (
                  <Progress value={file.progress} className="h-1.5 mt-1" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hidden file input shared by the desktop drop zone and the mobile button */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled || isUploading}
      />

      {/* Upload drop zone — desktop only (drag & drop) */}
      <div
        className={cn(
          'hidden sm:block border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer',
          isDragging && 'border-primary bg-primary/5',
          !isDragging && 'border-border hover:border-primary/50 hover:bg-muted/50',
          (disabled || isUploading) && 'opacity-50 cursor-not-allowed',
        )}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleBrowseClick();
          }
        }}
      >
        <div className="flex items-center justify-center gap-2">
          <Upload className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium">
            Drop images here or click to browse
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          JPEG, PNG, or WebP. Max 5 MB each. Multiple files supported.
        </p>
      </div>

      {/* Compact upload button — mobile only */}
      <Button
        type="button"
        variant="outline"
        className="sm:hidden w-full"
        onClick={handleBrowseClick}
        disabled={disabled || isUploading}
      >
        <Upload className="h-4 w-4 mr-2" />
        Upload photos
      </Button>

      {/* Add by URL */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Link className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            type="url"
            value={urlInput}
            onChange={(e) => { setUrlInput(e.target.value); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddUrl(); } }}
            placeholder="Or add image URL..."
            className="pl-8"
            disabled={disabled}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleAddUrl}
          disabled={disabled || !urlInput.trim()}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 text-destructive">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p className="text-sm whitespace-pre-line">{error}</p>
        </div>
      )}

      {required && values.length === 0 && !isUploading && (
        <p className="text-xs text-muted-foreground">
          At least one image is required
        </p>
      )}
    </div>
  );
}
